import React from "react";

import { styled, themeGet, useMedia, useTheme } from "@catchoftheday/theme";

import { Button } from "../Button";
import { Flex, FlexProps } from "../Flex";
import { IconSearch } from "../Icon";
import { InputDropdown, InputDropdownProps } from "../InputDropdown";
import { LoadingSpinner } from "../LoadingSpinner";

export type SearchBarProps<T> = {
  /** Event handler when search button is clicked */
  onSearchButtonClicked?: () => void;
  /** Event handler when search bar is focused */
  onInputFocus?: () => void;
  /** Props for SearchBar flex container */
  flexContainerProps?: FlexProps;
  /** Loading status */
  isLoading?: boolean;
} & InputDropdownProps<T>;

const StyledIconButton = styled(Button)`
  &:active {
    box-shadow: ${themeGet("shadows.button")};
  }
`;

export const SearchBar = <T extends unknown>({
  data = [],
  inputProps,
  onItemHighLighted,
  onItemSelected,
  dropdownContainerProps,
  onSearchButtonClicked,
  onInputFocus,
  flexContainerProps,
  isLoading,
  ...props
}: SearchBarProps<T>) => {
  const theme = useTheme();
  const isDesktop = useMedia<boolean>(
    [`(min-width: ${theme.breakpoints.md})`],
    [true]
  );

  return (
    <Flex {...flexContainerProps}>
      <Flex flexGrow={1} position="relative">
        <InputDropdown
          data={data}
          onItemHighLighted={onItemHighLighted}
          onItemSelected={onItemSelected}
          inputProps={{
            border: 1,
            borderColor: "ui.borderColor",
            borderRadius: "4px 0px 0px 4px",
            fontWeight: "light",
            p: 0,
            px: { sm: "10px", md: "26px" },
            "aria-label": "Search",
            placeholder: !isDesktop
              ? "Search"
              : "Search for products and brands...",
            height: { sm: "35px", md: "47px" },
            focusBorderColor: "tertiary",
            id: "searchbar",
            ...inputProps,
          }}
          dropdownContainerProps={{
            maxHeight: "250px",
            overflow: "auto",
            boxShadow: "dropDown",
            ...dropdownContainerProps,
          }}
          {...props}
          onInputFocus={onInputFocus}
        />
        {isLoading && (
          <LoadingSpinner
            data-testid="search-loading-spinner"
            position="absolute"
            width={{ xs: "20px", md: "25px" }}
            height={{ xs: "20px", md: "25px" }}
            right={{ xs: "5px", md: "10px" }}
            top={{ xs: "8px", md: "12px" }}
            ringSize="3px"
          />
        )}
      </Flex>
      <StyledIconButton
        aria-label="Search"
        sizeVariant="sm"
        variant="tertiary"
        outline
        bg="white"
        border="1px solid"
        borderColor="ui.borderColor"
        borderLeft="none"
        borderTopLeftRadius={0}
        borderBottomLeftRadius={0}
        height={{ sm: "35px", md: "47px" }}
        py={{ sm: "0px", md: "12px" }}
        onClick={onSearchButtonClicked}
      >
        <IconSearch size={25} />
      </StyledIconButton>
    </Flex>
  );
};

SearchBar.displayName = "SearchBar";

export default SearchBar;
